// ML001-F0219 (WebUI #170): the credit application settings, pure. Ledger design ML001-D013 section 2.60.12.15.
// Precedence on the wire: credit > customer > org > the default (automatic at 100%). Money is major units (pounds) on the policy wire.
// Nothing here decides a rule: the Ledger refuses; this file only shapes the form and the request.

// Labels follow the owner ruling (2026-10-09): credit is optional PER CREDIT, either automatic on the invoice (the dancers) or customer
// controlled (Edyta). "Admin by hand" is the Ledger's third mode (org and customer records only); the ruling does not name it.
export const POLICY_MODES = Object.freeze([
  { value: 'auto', label: 'Auto on invoice' },
  { value: 'admin', label: 'Admin by hand' },
  { value: 'customer', label: 'Customer controlled' },
]);
// A credit note that has no mode of its own follows the customer's preference. The Ledger stores that as mode null on a credit record
// (Spec CreditPolicyChanges.mode: null inherits the customer, then the org). The form shows it as this option; it is offered on a credit only.
export const FOLLOW_CUSTOMER = Object.freeze({ value: 'follow', label: 'Follow customer preference' });

// M004-F0754 (WebUI #197): the "Percent of the invoice" share, Ledger ML001-F0275 (Spec core-ledger-account-gateway 1.16.0). The option
// is a build-time flag exported in the build and restore jobs of .gitlab-ci.yml; it is 1 since Ledger #172 went live on prod (the UI must
// not offer what the Ledger refuses). An existing percent_of_invoice record always shows its option.
export const PERCENT_OF_INVOICE = 'percent_of_invoice';
export const isPercentOfInvoiceShareEnabled = () => process.env.REACT_APP_CREDIT_PERCENT_OF_INVOICE_ENABLED === '1';
// The share that takes a percent (the line share and the invoice share); the fixed share takes an amount.
export const isPercentShare = (discountType) => discountType === 'percent' || discountType === PERCENT_OF_INVOICE;

export const POLICY_LEVEL_LABEL = Object.freeze({ org: 'Organisation default', customer: 'This customer', credit: 'This credit note' });

// The credit-application control (owner's final design, WebUI #197): ONE manual concept. Automatic is mode auto; Manual is mode customer
// (the customer chooses after the invoice is issued). Admin by hand is never offered on a new record; it stays in the Mode select only when
// the record already holds it, so no stored value is silently dropped from the form.
export const AUTOMATIC_MODE = 'auto';
export const MANUAL_MODE = 'customer';
export const MANUAL_LINE = 'Never added to an invoice automatically. The customer decides on each issued invoice whether to use it; a manager approves unless self-service is on.';
// The control's checked value for a form mode; null (no radio checked) for admin and follow, which the control does not express.
export const creditApplicationOf = (mode) => (mode === AUTOMATIC_MODE || mode === MANUAL_MODE ? mode : null);

// The mode choices for a level. A customer, org or credit record offers the ruling's two modes; admin is appended only when the record holds it.
export function modeOptionsFor(level, current) {
  const options = level === 'credit' ? [FOLLOW_CUSTOMER, POLICY_MODES[0], POLICY_MODES[2]] : [POLICY_MODES[0], POLICY_MODES[2]];
  if (current !== 'admin') return options;
  const [first, ...rest] = options;
  return [first, POLICY_MODES[1], ...rest];
}

// The customer's three states (owner 2026-10-14; Ledger ML001-D013 section 2.60.12.15.2b): INHERIT the organisation default (the normal case, no
// record needed), CUSTOM terms for this customer (mode inherited or set), CUSTOMER-controlled (mode customer). Going back to Inherit is the
// Ledger's explicit `inherit: true`; there is no hidden null.
export const CUSTOMER_STATES = Object.freeze([
  { value: 'inherit', label: 'Use organisation default (inherit)', line: 'The organisation default applies to this customer: its share, caps, dates and mode.' },
  { value: 'custom', label: 'Custom for this customer', line: 'This customer has their own share, caps and dates, overriding the organisation default.' },
  { value: 'controlled', label: 'Customer controls when credit applies', line: MANUAL_LINE },
]);
export const customerStateLabel = (state) => CUSTOMER_STATES.find((item) => item.value === state)?.label || CUSTOMER_STATES[0].label;
// The state the Ledger's answer is in. Controlled wins (the mode is the customer's); Custom is a customer record that holds terms.
export function customerStateOf(answer) {
  if (answer?.record?.mode === MANUAL_MODE && answer?.effective?.mode_origin === 'customer') return 'controlled';
  if (answer?.effective?.terms_origin === 'customer') return 'custom';
  return 'inherit';
}

// The org default when no org record exists: automatic at 100%, no caps, no window.
export const DEFAULT_TERMS = Object.freeze({
  discount_type: 'percent', percent: 100, fixed_amount: null, max_per_booking: null, max_per_invoice: null,
  valid_from: null, expires_on: null, line_kinds: Object.freeze([]),
});

export const modeLabel = (mode) => POLICY_MODES.find((item) => item.value === mode)?.label || POLICY_MODES[0].label;

// A credit record with no mode of its own follows the customer (see FOLLOW_CUSTOMER).
export const followsCustomer = (answer) => answer?.level === 'credit' && (answer.record == null || answer.record.mode == null);

// "Automatic at 100%" / "£20 fixed" etc., read from the effective terms the Ledger answered.
export function termsSummary(terms) {
  const t = terms || DEFAULT_TERMS;
  if (t.discount_type === 'fixed') return `£${Number(t.fixed_amount || 0).toFixed(2)} fixed`;
  return t.discount_type === PERCENT_OF_INVOICE ? `${Number(t.percent)}% of the invoice total` : `${Number(t.percent)}%`;
}

// The form is strings so the inputs stay controlled; the request converts them.
export function formFromPolicy(answer) {
  const effective = answer?.effective || {};
  const terms = { ...DEFAULT_TERMS, ...(effective.terms || {}) };
  return {
    mode: followsCustomer(answer) ? FOLLOW_CUSTOMER.value : (effective.mode || 'auto'),
    // Customer level only: the state, and the mode of a Custom customer (follow = the organisation's mode, auto = their own).
    customer_state: customerStateOf(answer),
    custom_mode: answer?.level === 'customer' && answer?.record?.mode === 'auto' ? 'auto' : 'follow',
    discount_type: terms.discount_type || 'percent',
    percent: terms.percent == null ? '' : String(terms.percent),
    fixed_amount: terms.fixed_amount == null ? '' : String(terms.fixed_amount),
    max_per_booking: terms.max_per_booking == null ? '' : String(terms.max_per_booking),
    max_per_booking_kept: '', // the cap per booking while the invoice share is chosen: kept, never sent, restored on switching back
    max_per_invoice: terms.max_per_invoice == null ? '' : String(terms.max_per_invoice),
    valid_from: terms.valid_from || '',
    expires_on: terms.expires_on || '',
    line_kinds: Array.isArray(terms.line_kinds) ? terms.line_kinds.join(', ') : '',
    reason: '',
  };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const KIND_RE = /^[a-z][a-z_]{0,39}$/;

// The allowed charge types are the Ledger's own line kinds (charge_type, else source). The Ledger has no closed list the WebUI can
// copy, so the field takes the names as typed, comma separated; an empty field means every hire line is eligible.
export function parseLineKinds(text) {
  const kinds = String(text || '').split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
  return [...new Set(kinds)];
}
export const lineKindsError = (text) => (parseLineKinds(text).every((kind) => KIND_RE.test(kind)) ? null : 'Use lowercase charge type names, separated by commas.');
const toNumberOrNull = (value) => (value === '' || value == null ? null : Number(value));

// Returns { field: message }. An empty object means the form may be sent.
export function validatePolicyForm(form, level) {
  const errors = {};
  // Customer level: only Custom carries terms; Inherit and Customer-controlled need only the reason.
  if (level === 'customer' && form.customer_state !== 'custom') {
    if (!String(form.reason || '').trim()) errors.reason = 'Give a reason for this change.';
    return errors;
  }
  // Manual sends no share and no caps (the customer decides), so they are not validated either.
  if (form.mode !== MANUAL_MODE) {
    if (isPercentShare(form.discount_type)) {
      const p = toNumberOrNull(form.percent);
      if (p == null || !Number.isFinite(p) || p <= 0 || p > 100) errors.percent = 'Enter a percentage above 0 and up to 100.';
    } else {
      const f = toNumberOrNull(form.fixed_amount);
      if (f == null || !Number.isFinite(f) || f <= 0) errors.fixed_amount = 'Enter a fixed amount above £0.';
    }
    ['max_per_booking', 'max_per_invoice'].forEach((field) => {
      const v = toNumberOrNull(form[field]);
      if (v != null && (!Number.isFinite(v) || v <= 0)) errors[field] = 'A cap must be above £0, or empty for no cap.';
    });
  }
  if (form.valid_from && !ISO_DAY.test(form.valid_from)) errors.valid_from = 'Use a date.';
  if (form.expires_on && !ISO_DAY.test(form.expires_on)) errors.expires_on = 'Use a date.';
  if (form.valid_from && form.expires_on && form.valid_from > form.expires_on) errors.expires_on = 'Valid until must be on or after valid from.';
  if (lineKindsError(form.line_kinds)) errors.line_kinds = lineKindsError(form.line_kinds);
  if (!String(form.reason || '').trim()) errors.reason = 'Give a reason for this change.';
  return errors;
}

// Only the fields that differ from the answer are sent (credit_policy.set `changes`), so an identical save is a no-op on the Ledger.
export function buildPolicyChanges(form, answer, level = answer?.level) {
  const before = formFromPolicy(answer);
  const changes = {};
  if (level === 'customer') return buildCustomerChanges(form, before);
  // "follow" is sent as mode null (credit records only); every other mode is sent as its own value.
  const wireMode = (mode) => (mode === FOLLOW_CUSTOMER.value ? null : mode);
  if (form.mode !== before.mode) changes.mode = wireMode(form.mode);
  // Manual (mode customer) sends no share and no caps: the Share, percent and cap fields are disabled under it, and the values they hold
  // are kept in the form, not sent. Switching back to Automatic sends them again only where they differ from the answer.
  if (form.mode !== MANUAL_MODE) {
    if (form.discount_type !== before.discount_type) changes.discount_type = form.discount_type;
    if (isPercentShare(form.discount_type) && form.percent !== before.percent) changes.percent = Number(form.percent);
    if (form.discount_type === 'fixed' && form.fixed_amount !== before.fixed_amount) changes.fixed_amount = Number(form.fixed_amount);
    if (form.max_per_booking !== before.max_per_booking && form.discount_type !== PERCENT_OF_INVOICE) changes.max_per_booking = toNumberOrNull(form.max_per_booking);
    if (form.max_per_invoice !== before.max_per_invoice) changes.max_per_invoice = toNumberOrNull(form.max_per_invoice);
    // The invoice share never carries a cap per booking: the Ledger refuses a non-null value (ML001-F0274 test 007). The field is sent as
    // null on every save that changes something, never omitted for this share. An empty save stays empty ("Nothing has changed.").
    if (hasChanges(changes) && form.discount_type === PERCENT_OF_INVOICE) changes.max_per_booking = null;
  }
  ['valid_from', 'expires_on'].forEach((field) => {
    if (form[field] !== before[field]) changes[field] = form[field] || null;
  });
  const kinds = parseLineKinds(form.line_kinds);
  if (JSON.stringify(kinds) !== JSON.stringify(parseLineKinds(before.line_kinds))) changes.line_kinds = kinds;
  // The first save of an organisation with no policy record yet: the form shows the inherited mode without it being stored, so the
  // diff above never carries it, and a Ledger that needs a mode on a new org or customer record refused the save. Send the shown mode with
  // the first terms. An unchanged form still sends nothing ("Nothing has changed.").
  if (level === 'org' && answer?.record == null && hasChanges(changes) && !('mode' in changes)) changes.mode = wireMode(form.mode);
  return changes;
}

export const hasChanges = (changes) => Object.keys(changes).length > 0;

// Read-back check (owner bug 2026-10-14: a Ledger accepted a customer's fixed amount and dropped it, and the card showed Saved.). After a save,
// the effective terms the Ledger now answers must hold every term that was sent. Returns the names of the terms that did not stick.
const sameTerm = (a, b) => (Array.isArray(a) || Array.isArray(b) ? JSON.stringify(a ?? []) === JSON.stringify(b ?? []) : (a ?? null) === (b ?? null));
export function lostTerms(changes, freshAnswer) {
  const terms = freshAnswer?.effective?.terms;
  if (!terms) return [];
  // Going back to Inherit: the customer's own mode or terms must be gone.
  if (changes.inherit === true) {
    return freshAnswer.effective.terms_origin === 'customer' || freshAnswer.effective.mode_origin === 'customer' ? ['inherit'] : [];
  }
  return ['discount_type', 'percent', 'fixed_amount', 'max_per_booking', 'max_per_invoice', 'valid_from', 'expires_on', 'line_kinds']
    .filter((key) => Object.prototype.hasOwnProperty.call(changes, key) && !sameTerm(changes[key], terms[key]));
}

const CUSTOMER_TERM_FIELDS = ['discount_type', 'percent', 'fixed_amount', 'max_per_booking', 'max_per_invoice', 'valid_from', 'expires_on', 'line_kinds'];

// The customer card's request. Inherit sends only `inherit: true` (and nothing when already inherited); Customer-controlled sends the mode;
// Custom sends the terms (the whole set on entering Custom, only what differs afterwards) and the mode choice (follow = null, auto).
function buildCustomerChanges(form, before) {
  const state = form.customer_state;
  if (state === 'inherit') return before.customer_state === 'inherit' ? {} : { inherit: true };
  if (state === 'controlled') return before.customer_state === 'controlled' ? {} : { mode: MANUAL_MODE };
  const entering = before.customer_state !== 'custom';
  const changes = {};
  const wanted = form.custom_mode === 'auto' ? 'auto' : null;
  const had = before.customer_state === 'custom' ? (before.custom_mode === 'auto' ? 'auto' : null) : undefined;
  if (wanted !== had) changes.mode = wanted;
  const share = form.discount_type;
  const send = (field, value) => { changes[field] = value; };
  CUSTOMER_TERM_FIELDS.forEach((field) => {
    if (field === 'percent' && !isPercentShare(share)) return;
    if (field === 'fixed_amount' && share !== 'fixed') return;
    if (field === 'max_per_booking' && share === PERCENT_OF_INVOICE) return;
    const value = field === 'line_kinds' ? parseLineKinds(form.line_kinds)
      : (field === 'valid_from' || field === 'expires_on') ? (form[field] || null)
        : field === 'discount_type' ? form.discount_type
          : toNumberOrNull(form[field]);
    const was = field === 'line_kinds' ? parseLineKinds(before.line_kinds)
      : (field === 'valid_from' || field === 'expires_on') ? (before[field] || null)
        : field === 'discount_type' ? before.discount_type : toNumberOrNull(before[field]);
    if (entering || JSON.stringify(value) !== JSON.stringify(was)) send(field, value);
  });
  // The invoice share never carries a cap per booking (ML001-F0274 test 007): sent as null whenever the terms are sent.
  if (share === PERCENT_OF_INVOICE && (entering || Object.keys(changes).length)) changes.max_per_booking = null;
  return changes;
}
